import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { Chessboard } from 'react-chessboard';
import { fenToPositionObj } from './ChessLessonView';

// ─────────────────────────────────────────────────────
// Tutor board — Ms. Momo's own board ("Watch Ms. Momo"). Display-only: the
// student never interacts with it.
//
// When idle it MIRRORS the student board (position + square styles). The lesson
// calls playDemo() through a ref to make Ms. Momo demonstrate: a pointing hand
// glides to the piece, lifts it, carries it square by square through the whole
// path (Rule 20 — Knight shows both legs), yellow path dots light as it passes
// (Rule 6), then it drops the piece and the from/to squares are tinted. Multi-frame
// demos return to the start position between frames (Rule 13).
//
// All timers live in one ref array; release() clears them and is called on every
// step change (Rule 14/23). Callbacks only touch this component's own state; the
// lesson guards its own state with currentStepRef in its onDone callbacks.
// No transform/scale/zoom is applied to any ancestor of the board (Rule 27).
// ─────────────────────────────────────────────────────

const FILES = 'abcdefgh';
// Computed lazily: this module and ChessLessonView import each other.
let START_POS = null;
const startPos = () => (START_POS || (START_POS = fenToPositionObj('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1')));
const STEP_MS = 170;   // per square of travel

// Any position form (FEN string, 'start', 'empty', position object) -> position object.
export function toPositionObj(p) {
  if (!p || p === 'empty' || p === 'empty-custom') return {};
  if (p === 'start') return { ...startPos() };
  if (typeof p === 'object') return { ...p };
  const obj = fenToPositionObj(p);
  return typeof obj === 'object' ? obj : {};
}

// The square that lost a piece and the square that gained it (the move made).
export function diffMove(start, end) {
  let from = null, to = null;
  for (const sq of Object.keys(start)) {
    if (start[sq] && end[sq] !== start[sq]) {
      // candidate: does that piece appear somewhere new in `end`?
      const code = start[sq];
      const dest = Object.keys(end).find(s => end[s] === code && start[s] !== code);
      if (dest) { from = sq; to = dest; break; }
    }
  }
  return { from, to };
}

const fileIdx = sq => FILES.indexOf(sq[0]);
const rankNum = sq => Number(sq[1]);
const sqAt = (f, r) => FILES[f] + r;

// Squares the piece passes through from `from` to each target in turn (excluding
// `from`, including the destination). Straight/diagonal lines are filled square by
// square; a knight jump is broken into its two legs (longer leg first).
export function expandRoute(from, path, to) {
  const targets = [...(path && path.length ? path : [])];
  if (to && targets[targets.length - 1] !== to) targets.push(to);
  const route = [];
  let cur = from;
  for (const t of targets) {
    if (!cur || !t || t === cur) { cur = t; continue; }
    const df = fileIdx(t) - fileIdx(cur), dr = rankNum(t) - rankNum(cur);
    const adf = Math.abs(df), adr = Math.abs(dr);
    if ((adf === 1 && adr === 2) || (adf === 2 && adr === 1)) {
      // knight: long leg first, then the short leg
      let f = fileIdx(cur), r = rankNum(cur);
      if (adr === 2) { r += Math.sign(dr); route.push(sqAt(f, r)); r += Math.sign(dr); route.push(sqAt(f, r)); f += Math.sign(df); }
      else { f += Math.sign(df); route.push(sqAt(f, r)); f += Math.sign(df); route.push(sqAt(f, r)); r += Math.sign(dr); }
      route.push(sqAt(f, r));
    } else if (adf === 0 || adr === 0 || adf === adr) {
      const sf = Math.sign(df), sr = Math.sign(dr);
      let f = fileIdx(cur), r = rankNum(cur);
      for (let k = 0; k < Math.max(adf, adr); k++) { f += sf; r += sr; route.push(sqAt(f, r)); }
    } else {
      route.push(t);
    }
    cur = t;
  }
  return route;
}

const centre = sq => ({ x: (fileIdx(sq) + 0.5) * 12.5, y: (8 - rankNum(sq) + 0.5) * 12.5 });

const HAND_SVG = (
  <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M24 8c3 0 5 2 5 5v17l2-1V20c0-3 2-5 5-5s5 2 5 5v10l1 .3V24c0-3 2-5 5-5s5 2 5 5v18c0 11-8 18-18 18h-3c-6 0-10-3-13-8L8 40c-2-3-1-6 2-7 2-1 5 0 6 2l3 5V13c0-3 2-5 5-5z" fill="#FFE2C2" stroke="#0A0A0A" strokeWidth="3" strokeLinejoin="round"/></svg>
);

const TutorBoard = forwardRef(function TutorBoard({
  position, size, squareStyles, customPieces, lightSquareStyle, darkSquareStyle,
  pieceSrc, talking, onBusyChange,
}, ref) {
  const boardPx = Math.max(120, Math.floor(size) - 12);
  const [override, setOverride] = useState(null);   // position shown instead of the mirror
  const [marks, setMarks] = useState({});           // { from, to, sel }
  const [dots, setDots] = useState([]);             // path squares already passed
  const [hand, setHand] = useState({ on: false, x: 96, y: 96, press: false, dur: 0 });
  const [lift, setLift] = useState(null);           // { code, x, y, dur }
  const [busy, setBusy] = useState(false);
  const [hasDemo, setHasDemo] = useState(false);
  const timers = useRef([]);
  const lastDemo = useRef(null);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  function clearAll() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }
  function setBusyFlag(b) { setBusy(b); onBusyChange?.(b); }

  function resetOverlay() {
    setMarks({}); setDots([]); setLift(null);
    setHand(h => ({ ...h, on: false, press: false, dur: 0 }));
  }

  // Plays one frame starting at time t0 (ms from now). Returns the frame's end time.
  function scheduleFrame(t0, startObj, frame) {
    const endObj = toPositionObj(frame.end);
    let { from, to } = frame.from !== undefined || frame.to ? { from: frame.from, to: frame.to } : diffMove(startObj, endObj);
    if (!to && frame.path?.length) to = frame.path[frame.path.length - 1];
    if (from === undefined || (!from && !frame.offboard)) from = null;
    const code = frame.code || (from ? startObj[from] : endObj[to]);
    const entry = frame.offboard === 'above' ? { x: 50, y: -8 } : frame.offboard === 'below' ? { x: 50, y: 108 } : { x: 96, y: 96 };
    const route = from ? expandRoute(from, frame.path, to) : [to];
    const fromC = from ? centre(from) : entry;

    later(() => {
      setOverride(startObj); setMarks({}); setDots([]);
      setLift(frame.offboard ? { code, x: entry.x, y: entry.y, dur: 0 } : null);
      setHand({ on: true, x: entry.x, y: entry.y, press: !!frame.offboard, dur: 0 });
    }, t0);
    later(() => setHand({ on: true, x: fromC.x, y: fromC.y, press: !!frame.offboard, dur: 550 }), t0 + 40);
    let t = t0 + 650;
    if (from) {
      later(() => {
        const lifted = { ...startObj }; delete lifted[from];
        setOverride(lifted);
        setMarks({ sel: from });
        setLift({ code, x: fromC.x, y: fromC.y, dur: 0 });
        setHand(h => ({ ...h, press: true }));
      }, t);
      t += 150;
    }
    route.forEach((sq, k) => {
      const c = centre(sq);
      later(() => {
        setHand(h => ({ ...h, x: c.x, y: c.y, dur: STEP_MS }));
        setLift(l => (l ? { ...l, x: c.x, y: c.y, dur: STEP_MS } : l));
        if (k < route.length - 1) setDots(d => [...d, sq]);
      }, t + k * STEP_MS);
    });
    t += route.length * STEP_MS + 60;
    later(() => {
      setLift(null);
      setOverride(endObj);
      setMarks({ from, to });
      setHand(h => ({ ...h, press: false }));
    }, t);
    later(() => setHand(h => ({ ...h, on: false, dur: 250 })), t + 400);
    return t + 500;
  }

  function playDemo({ start, frames, holdLast = true, onDone }, isReplay = false) {
    clearAll();
    resetOverlay();
    if (!isReplay) { lastDemo.current = { start, frames, holdLast }; setHasDemo(true); }
    const startObj = toPositionObj(start);
    setBusyFlag(true);
    let t = 0;
    frames.forEach((frame, i) => {
      const isLast = i === frames.length - 1;
      const end = scheduleFrame(t, startObj, frame);
      t = end + 700;                       // let the student see where it landed
      if (!isLast || !holdLast) {
        later(() => { setOverride(startObj); setMarks({}); setDots([]); }, t);
        t += 350;
      }
    });
    later(() => { setBusyFlag(false); onDone?.(); }, t);
  }

  useImperativeHandle(ref, () => ({
    playDemo,
    // Back to mirroring the student board (step change / demo no longer relevant).
    release() { clearAll(); resetOverlay(); setOverride(null); setBusyFlag(false); lastDemo.current = null; setHasDemo(false); },
    isBusy: () => busy,
  }));

  useEffect(() => () => clearAll(), []);

  function replay() {
    if (!lastDemo.current || busy) return;
    playDemo(lastDemo.current, true);
  }

  // Tutor's own marks while it demonstrates; otherwise mirror the student styles.
  const markStyles = {};
  if (marks.sel) markStyles[marks.sel] = { backgroundColor: 'rgba(34,164,93,0.45)' };
  if (marks.from) markStyles[marks.from] = { backgroundColor: 'rgba(255,212,59,0.6)' };
  if (marks.to) markStyles[marks.to] = { backgroundColor: 'rgba(255,212,59,0.6)' };
  const shownPosition = override || fenToPositionObj(position);
  const shownStyles = override ? markStyles : squareStyles;

  return (
    <div className="cl-watch-card">
      <div className="cl-watch-head">
        <span className="cl-watch-title">
          <span className={`cl-watch-owl${talking ? ' cl-talk' : ''}`}><img src="/images/momo-owl.png" alt="" draggable={false} /></span>
          Watch Ms. Momo
        </span>
        {/* Replays Ms. Momo's last demonstration. Disabled while she speaks (Rule 1). */}
        <button className="cl-watch-again" disabled={!hasDemo || busy || talking} onClick={replay}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.6-5.9" /><path d="M20 4v5h-5" /></svg>
          Show again
        </button>
      </div>
      <div className="cl-watch-zone">
        <div className="cl-tutor-frame">
          <div className="cl-tutor-inner">
            <Chessboard
              key={`w${boardPx}`}  // react-chessboard 4.x reads boardWidth only on mount
              id="tso-tutor-board"
              // fenToPositionObj uses skipValidation (Rule 11); position objects pass through unchanged.
              position={shownPosition}
              boardWidth={boardPx}
              boardOrientation="white"
              arePiecesDraggable={false}
              showAnimations={!override}
              animationDuration={override ? 0 : 500}
              showBoardNotation={true}
              customSquareStyles={shownStyles}
              customLightSquareStyle={lightSquareStyle}
              customDarkSquareStyle={darkSquareStyle}
              customPieces={customPieces}
            />
            <div className="cl-tutor-fx" aria-hidden="true">
              {dots.map(sq => {
                const c = centre(sq);
                return <span key={sq} className="cl-tutor-dot" style={{ left: `${c.x}%`, top: `${c.y}%` }} />;
              })}
              {lift && pieceSrc?.[lift.code] && (
                <img className="cl-tutor-lift" src={pieceSrc[lift.code]} alt="" draggable={false}
                  style={{ left: `${lift.x}%`, top: `${lift.y}%`, transition: `left ${lift.dur}ms ease-in-out, top ${lift.dur}ms ease-in-out` }} />
              )}
              <div className={`cl-tutor-hand${hand.press ? ' cl-press' : ''}`}
                style={{ left: `${hand.x}%`, top: `${hand.y}%`, opacity: hand.on ? 1 : 0,
                  transition: `left ${hand.dur}ms ease-in-out, top ${hand.dur}ms ease-in-out, opacity 250ms` }}>
                {HAND_SVG}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TutorBoard;
